import { useCallback, useEffect, useState } from 'react';

import { Button, Card, Typography } from 'antd';

import useStepStore from '@/stores/steps';

import Message from '../Message';
import Options, { type AnswerBlockOption } from './Options';

export type AnswerBlockProps = {
  title?: string;
  resetTrigger?: boolean;
  options: AnswerBlockOption[];
  type?: 'single' | 'multiple';
  onSelectOption?: (value: string[]) => void;
  awaitOnAnswer?: (answer: string[]) => Promise<void>;
};

const AnswerBlock = ({
  resetTrigger,
  options,
  title,
  type = 'single',
  onSelectOption,
  awaitOnAnswer
}: AnswerBlockProps) => {
  const nextStep = useStepStore(({ nextStep }) => nextStep);

  const [selection, setSelection] = useState<string[]>([]);
  const [selectionText, setSelectionText] = useState<string>('');

  useEffect(() => {
    if (resetTrigger !== undefined) {
      setSelection([]);
      setSelectionText('');
    }
  }, [resetTrigger]);

  const emitAnswer = useCallback(
    async (selection: string[]) => {
      if (onSelectOption) {
        onSelectOption(selection);
      }

      if (awaitOnAnswer) {
        await awaitOnAnswer(selection);
      }

      setSelectionText(
        options
          .filter(item => selection.includes(item.value))
          .map(item => item.content.title || item.content.description)
          .join(', ')
      );

      const skipCount =
        options.filter(option => selection.includes(option.value)).sort((a, b) => (b.skip || 1) - (a.skip || 1))[0]
          ?.skip || 1;

      nextStep({ skip: skipCount });
    },
    [onSelectOption, awaitOnAnswer, options, nextStep]
  );

  const handleSelectOption = useCallback(
    async (value: string, isSelected: boolean) => {
      if (type === 'multiple') {
        setSelection(current => {
          if (isSelected) {
            return current.filter(item => item !== value);
          }

          return [...current, value];
        });
        return;
      }

      emitAnswer([value]);
    },
    [type, emitAnswer]
  );

  const confirmSelection = useCallback(() => {
    emitAnswer(selection);
  }, [emitAnswer, selection]);

  if (selectionText) {
    return <Message type='answer'>{selectionText}</Message>;
  }

  return (
    <>
      <Card size='small' className='bg-(--uizz-antd-color-fill-tertiary)'>
        {title && <Typography.Title level={5}>{title}</Typography.Title>}

        <Options handleSelectOption={handleSelectOption} options={options} selection={selection} type={type} />
      </Card>

      {type !== 'single' && (
        <Button
          block
          variant='filled'
          color='default'
          className='mt-4'
          id='confirm-selection-button'
          onClick={confirmSelection}
        >
          Confirmar seleção
        </Button>
      )}
    </>
  );
};

export default AnswerBlock;
